import React from 'react';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { css } from '@emotion/react';
import { color } from '../../../../styles';
import { IGetDfreeBDeviceAnalyticsResult } from '../../../../types/api/traversal/getDfreeBDeviceAnalytics';
import { TimeDisplay } from './timeDisplay';
import { useChartResizeObserver } from '../../../../hooks/useChartResizeObserver';
import { useTranslation } from 'react-i18next';

interface Props {
  dailyLaxative: IGetDfreeBDeviceAnalyticsResult['dailyLaxative']['dailyLaxative'];
}

const GraphContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  border-radius: 8px;
  border: 1px solid ${color.border_gray};
  background: ${color.light_gray};
`;

const GraphTitle = css`
  max-width: 450px;
  font-size: 24px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 16px;
`;

const GraphSubtitle = css`
  margin-bottom: 12px;
`;

const AverageTimeContainer = css`
  display: flex;
  align-items: baseline;
  justify-content: center;
  background-color: #ffffff;
  border: 1px solid ${color.border_gray};
  width: 100%;
  max-width: 480px;
  border-radius: 8px;
  padding: 16px;
  gap: 2px;
  margin-bottom: 16px;
`;

export const DefecationBarChart: React.FC<Props> = ({ dailyLaxative }) => {
  const { t, i18n } = useTranslation();
  const chartRef = React.useRef<HighchartsReact.RefObject>(null);
  useChartResizeObserver(chartRef);
  const totalMinutes = dailyLaxative.reduce((sum, r) => sum + r.minutes, 0) / dailyLaxative.length;
  const formattedData = dailyLaxative
    .slice()
    .sort((a, b) => dayjs(b.timestamp).unix() - dayjs(a.timestamp).unix())
    .map((item) => {
      const dateLabel = dayjs(item.timestamp).format('M/D');
      const seconds = item.minutes * 60;
      return [dateLabel, seconds] as [string, number];
    });

  const labelFontSize = formattedData.length > 15 ? '12px' : '14px';

  const chartHeight = React.useMemo(() => {
    const minHeight = 360;
    const baseHeight = 40;
    const rowHeight = 16;
    return Math.max(minHeight, baseHeight + formattedData.length * rowHeight);
  }, [dailyLaxative]);

  const options: Highcharts.Options = React.useMemo(() => {
    return {
      chart: {
        type: 'bar',
        marginLeft: 100,
        marginRight: 40,
        height: chartHeight,
        width: 480,
        backgroundColor: 'transparent',
      },
      title: {
        text: '',
      },
      xAxis: {
        type: 'category',
        title: {
          y: i18n.language.startsWith('ja') ? 90 : 10,
          x: i18n.language.startsWith('ja') ? 50 : -12,
          text: t('dayWithDefecation', '排便のあった日'),
          align: 'high',
          style: {
            writingMode: i18n.language.startsWith('ja') ? 'vertical-rl' : 'initial',
            transform: i18n.language.startsWith('ja') ? 'none' : 'auto',
          },
        },
        labels: {
          overflow: 'justify',
          style: {
            fontSize: labelFontSize,
          },
        },
      },
      yAxis: {
        type: 'linear',
        min: 0,
        max: 30 * 60 * 60,
        tickInterval: 10 * 60 * 60,
        minorTickInterval: 2 * 60 * 60,
        labels: {
          formatter: function () {
            const totalSeconds = this.value as number;
            const hours = Math.floor(totalSeconds / (60 * 60));
            return `${hours}h`;
          },
        },
        title: {
          text: '',
        },
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
          },
          color: '#0CB2B4',
        },
      },
      series: [
        {
          type: 'bar',
          name: '排便間隔',
          data: formattedData,
          dataLabels: { enabled: false },
        },
      ],
      tooltip: {
        formatter: function () {
          const totalSeconds = this.y as number;
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          return `${hours}${t('hours', '時間')} ${minutes}${t('minutes', '分')}`;
        },
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    };
  }, [dailyLaxative]);

  return (
    <div css={GraphContainerStyle}>
      <div css={GraphTitle}>
        {t('timeFromAdministrationOfStimulantLaxativeToFirstBowelMovement', '刺激性下剤投与から初回排便までの時間')}
      </div>
      <div css={GraphSubtitle}>{t('soiledDataNotIncluded', '※付着データは含まれていません')}</div>
      <div css={AverageTimeContainer}>
        <TimeDisplay minutes={totalMinutes} />
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
    </div>
  );
};
